import React, { useState, useEffect } from 'react';
import confetti from 'canvas-confetti';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { CheckCircle, XCircle, RotateCcw, Trophy, Target, Loader2, AlertCircle, Settings } from 'lucide-react';
import { Checkbox } from './ui/checkbox';
import { quizAPI, handleAPIError } from '../services/api';

const QuizGame = () => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [gameQuestions, setGameQuestions] = useState([]);
  const [gameStarted, setGameStarted] = useState(false);
  const [quizComplete, setQuizComplete] = useState(false);
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [quizSession, setQuizSession] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [playerAnswers, setPlayerAnswers] = useState([]);
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [showDifficultySelection, setShowDifficultySelection] = useState(true);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showCategorySelection, setShowCategorySelection] = useState(false);
  const [categoryType, setCategoryType] = useState(null); // 'IGCSE' or 'KS3'
  const [showCategoryTypeSelection, setShowCategoryTypeSelection] = useState(true);
  const [igcseMenu, setIgcseMenu] = useState(false); // IGCSE sub-menu: Keyword Quiz vs Question Helper
  const navigate = useNavigate();

  useEffect(() => {
    initializeDatabase();
  }, []);

  const initializeDatabase = async () => {
    try {
      setLoading(true);
      // Seed questions if database is empty
      await quizAPI.seedQuestions();
      
      // Get available categories
      const categoriesData = await quizAPI.getCategories();
      setAvailableCategories(categoriesData.categories);
      const countsData = await quizAPI.getCategoryCounts();
      setCategoryCounts(countsData.counts || {});
      
      setError(null);
    } catch (err) {
      console.log('Database already seeded or error occurred:', err);
      // This is expected if questions are already seeded
      
      // Still try to get categories
      try {
        const categoriesData = await quizAPI.getCategories();
        setAvailableCategories(categoriesData.categories);
        const countsData = await quizAPI.getCategoryCounts();
        setCategoryCounts(countsData.counts || {});
      } catch (catErr) {
        console.log('Could not load categories:', catErr);
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for category management
  const getIGCSECategories = () => {
    return availableCategories.filter(cat => cat.startsWith('IGCSE'));
  };

  const getKS3Categories = () => {
    return availableCategories.filter(cat => cat.startsWith('KS3'));
  };

  // IBDP unit metadata: official ordering, unit code labels, and SL/HL level.
  const IBDP_TOPIC_META = {
    'IBDP Population Distribution—Changing Population': { label: 'Core 1', order: 1, level: 'SL and HL' },
    'IBDP Global Climate—Vulnerability and Resilience': { label: 'Core 2', order: 2, level: 'SL and HL' },
    'IBDP Global Resource Consumption and Security': { label: 'Core 3', order: 3, level: 'SL and HL' },
    'IBDP Freshwater': { label: 'Option A', order: 4, level: 'SL and HL' },
    'IBDP Oceans and Coastal Margins': { label: 'Option B', order: 5, level: 'SL and HL' },
    'IBDP Extreme Environments': { label: 'Option C', order: 6, level: 'SL and HL' },
    'IBDP Geophysical Hazards': { label: 'Option D', order: 7, level: 'SL and HL' },
    'IBDP Leisure, Tourism and Sport': { label: 'Option E', order: 8, level: 'SL and HL' },
    'IBDP Food and Health': { label: 'Option F', order: 9, level: 'SL and HL' },
    'IBDP Urban Environments': { label: 'Option G', order: 10, level: 'SL and HL' },
    'IBDP Power, Places and Networks': { label: 'HL 1', order: 11, level: 'HL only' },
    'IBDP Human Development and Diversity': { label: 'HL 2', order: 12, level: 'HL only' },
    'IBDP Global Risks and Resilience': { label: 'HL 3', order: 13, level: 'HL only' },
  };

  const getIBDPCategories = () => {
    return availableCategories
      .filter(cat => cat.startsWith('IBDP'))
      .sort((a, b) => (IBDP_TOPIC_META[a]?.order || 99) - (IBDP_TOPIC_META[b]?.order || 99));
  };

  const getAvailableQuestionsCount = () => {
    // Use the real per-category counts from the backend; fall back to an
    // estimate only if counts haven't loaded yet.
    return selectedCategories.reduce((total, cat) => {
      const count = categoryCounts[cat];
      return total + (typeof count === 'number' ? count : 25);
    }, 0);
  };

  const getValidDifficultyOptions = () => {
    const availableQuestions = getAvailableQuestionsCount();
    return difficultyOptions.filter(option => option.questions <= availableQuestions);
  };

  const getTopicDescription = (topicName) => {
    const descriptions = {
      // IGCSE Topics
      'Changing River Environments': 'River processes, landforms, and flood management',
      'Changing Coastal Environments': 'Coastal processes, landforms, and sea defences',
      'Changing Ecosystems': 'Biomes, biodiversity, and environmental management',
      'Tectonic Hazards': 'Plate tectonics, earthquakes, and volcanoes',
      'Climate Change': 'Greenhouse effect, impacts, and responses',
      'Changing Populations': 'Demographics, migration, and population policies',
      'Changing Towns and Cities': 'Urbanisation, planning, and sustainability',
      'Development': 'Measuring development, aid, and trade',
      'Changing Economies': 'Economic sectors and globalisation',
      'Resource Provision': 'Energy, water, and food security',
      'Cartographic Skills': 'Maps, scale, and geographical techniques',
      
      // IBDP Topics
      'Population Distribution—Changing Population': 'Population patterns, change, and policies',
      'Global Climate—Vulnerability and Resilience': 'Climate processes, hazards, and responses',
      'Global Resource Consumption and Security': 'Resources, security, and the nexus',
      'Freshwater': 'Drainage basins, water management, and conflict',
      'Oceans and Coastal Margins': 'Ocean systems, coasts, and management',
      'Extreme Environments': 'Hot, cold, and high-altitude landscapes',
      'Geophysical Hazards': 'Earthquakes, volcanoes, and mass movement',
      'Leisure, Tourism and Sport': 'Tourism, recreation, and sport geography',
      'Food and Health': 'Food systems, disease, and wellbeing',
      'Urban Environments': 'Urbanisation, planning, and liveability',
      'Power, Places and Networks': 'Globalisation, power, and global interactions',
      'Human Development and Diversity': 'Development, culture, and human rights',
      'Global Risks and Resilience': 'Global risks, governance, and resilience',
      
      // KS3 Topics
      'Rivers': 'River processes and water cycle',
      'Coasts': 'Coastal processes and landforms',
      'Plate Tectonics': 'Earth structure and tectonic processes',
      'Tropical Rainforests': 'Rainforest ecosystems and sustainability',
      'Energy': 'Energy sources and sustainability',
      'Development': 'Measuring and comparing development',
      'Map Skills': 'Reading maps and geographical skills',
      'Middle East': 'Regional geography and development',
      'Oceans': 'Marine ecosystems and conservation',
      'Changing China': 'Economic development and change',
      'Africa': 'Regional geography and development'
    };
    
    return descriptions[topicName] || 'Geography topic';
  };

  const initializeGame = async (questionCount) => {
    try {
      setLoading(true);
      setError(null);
      
      // Start new quiz session with selected question count and categories
      const sessionData = await quizAPI.startQuiz(null, questionCount, selectedCategories);
      setQuizSession(sessionData);
      setGameQuestions(sessionData.questions);
      
      // Reset game state
      setCurrentQuestionIndex(0);
      setScore(0);
      setSelectedAnswer(null);
      setShowResult(false);
      setQuizComplete(false);
      setAnswerSubmitted(false);
      setPlayerAnswers([]);
      
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const startGame = async (difficulty) => {
    await initializeGame(difficulty.questions);
    setSelectedDifficulty(difficulty);
    setGameStarted(true);
    setStartTime(Date.now());
  };

  const selectCategoryType = (type) => {
    setCategoryType(type);
    setShowCategoryTypeSelection(false);
    setShowCategorySelection(true);
    setShowDifficultySelection(false);
    setSelectedCategories([]); // Reset selected categories when changing type
  };

  const toggleCategory = (category) => {
    setSelectedCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(cat => cat !== category);
      } else {
        return [...prev, category];
      }
    });
  };

  const backToCategoryType = () => {
    setShowCategorySelection(false);
    setShowCategoryTypeSelection(true);
    setShowDifficultySelection(false);
    setCategoryType(null);
    setSelectedCategories([]);
  };

  const proceedToDifficulty = () => {
    if (selectedCategories.length === 0) {
      setError('Please select at least one category before proceeding.');
      return;
    }
    setShowCategorySelection(false);
    setShowDifficultySelection(true);
    setError(null);
  };

  const backToCategories = () => {
    setShowDifficultySelection(false);
    setShowCategorySelection(true);
  };

  const difficultyOptions = [
    { 
      name: "Easy", 
      questions: 10, 
      description: "Perfect for beginners - covers basic geography terms",
      color: "from-green-500 to-emerald-600",
      hoverColor: "from-green-600 to-emerald-700"
    },
    { 
      name: "Medium", 
      questions: 25, 
      description: "Good challenge - mix of basic and intermediate terms",
      color: "from-blue-500 to-cyan-600",
      hoverColor: "from-blue-600 to-cyan-700"
    },
    { 
      name: "Hard", 
      questions: 50, 
      description: "Serious test - comprehensive geography knowledge",
      color: "from-orange-500 to-red-600",
      hoverColor: "from-orange-600 to-red-700"
    },
    { 
      name: "Professional Geographer", 
      questions: 100, 
      description: "Ultimate challenge - for geography experts only!",
      color: "from-purple-500 to-indigo-600",
      hoverColor: "from-purple-600 to-indigo-700"
    }
  ];

  const handleAnswerSelect = (optionId) => {
    if (!answerSubmitted) {
      setSelectedAnswer(optionId);
    }
  };

  const submitAnswer = () => {
    if (!selectedAnswer) return;
    
    setAnswerSubmitted(true);
    const currentQuestion = gameQuestions[currentQuestionIndex];
    const selectedOption = currentQuestion.options.find(opt => opt.id === selectedAnswer);
    const isCorrect = selectedOption.isCorrect;
    
    // Record the answer
    const answer = {
      question_id: currentQuestion.id,
      selected_answer: selectedOption.text,
      is_correct: isCorrect,
      time_taken: Math.floor((Date.now() - startTime) / 1000)
    };
    
    setPlayerAnswers(prev => [...prev, answer]);
    
    if (isCorrect) {
      setScore(score + 1);
    }
    
    setShowResult(true);
    // Remove automatic progression - user will click Next Question button
  };

  const nextQuestion = async () => {
    setShowResult(false);
    setSelectedAnswer(null);
    setAnswerSubmitted(false);
    
    if (currentQuestionIndex < gameQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      await completeQuiz();
    }
  };

  const completeQuiz = async () => {
    try {
      setLoading(true);
      const completionTime = Math.floor((Date.now() - startTime) / 1000);
      
      // Submit quiz results to backend
      await quizAPI.submitQuiz(quizSession.session_id, playerAnswers, completionTime);
      setQuizComplete(true);
      
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const restartGame = () => {
    setGameStarted(false);
    setQuizComplete(false);
    setShowDifficultySelection(false);
    setShowCategorySelection(false);
    setShowCategoryTypeSelection(true);
    setSelectedDifficulty(null);
    setCategoryType(null);
    setSelectedCategories([]);
    setError(null);
  };

  const getScoreMessage = () => {
    const percentage = (score / gameQuestions.length) * 100;
    if (percentage >= 90) return "Outstanding! Geography expert! 🌟";
    if (percentage >= 70) return "Great job! Well done! 👏";
    if (percentage >= 50) return "Good effort! Keep studying! 📚";
    return "Don't give up! Practice makes perfect! 💪";
  };

  const triggerConfetti = () => {
    // Multiple confetti bursts for 100% celebration
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      
      // Left side
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      
      // Right side  
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);
  };

  // Check for perfect score and trigger confetti
  useEffect(() => {
    if (quizComplete && score === gameQuestions.length && gameQuestions.length > 0) {
      // Perfect score! Trigger confetti after a short delay
      const timer = setTimeout(() => {
        triggerConfetti();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [quizComplete, score, gameQuestions.length]);

  const getProgressPercentage = () => {
    return ((currentQuestionIndex + 1) / gameQuestions.length) * 100;
  };

  const getSelectedAnswerExplanation = (selectedText) => {
    // Comprehensive dictionary of ALL geography terms that can appear as answer options
    // This includes all keywords from backend categories plus additional common terms
    const definitions = {
      // IGCSE Changing River Environments - manually curated accurate definitions
      "River basin": "The area of land drained by a river and its tributaries.",
      "Watershed": "The high land dividing one river basin from another.",
      "Source": "The starting point of a river.",
      "Mouth": "Where a river flows into the sea, lake, or ocean.",
      "Upper course": "Steep part of a river with V-shaped valleys and waterfalls.",
      "Middle course": "River section with meanders and wider valleys.",
      "Lower course": "Flat section near the mouth with floodplains and deltas.",
      "Long profile": "The side view of a river from source to mouth.",
      "Cross profile": "A slice across a river valley showing its shape.",
      "Erosion": "The wearing away of land by natural forces.",
      "Hydraulic action": "Water force breaking rock by entering cracks.",
      "Corrasion (abrasion)": "Rock fragments carried by a river scraping against the bed and banks.",
      "Attrition": "Rock fragments carried by a river hitting each other and breaking into smaller, smoother pieces.",
      "Solution (corrosion)": "Minerals dissolved in river water.",
      "Transportation": "The movement of eroded material by the river.",
      "Traction": "Large rocks rolled along the river bed.",
      "Saltation": "Small pebbles bounced along the river bed.",
      "Suspension": "Fine material like silt carried in the water.",
      "Solution (transportation)": "Dissolved material carried in the river.",
      "Deposition": "The river dropping material when it loses energy.",
      "Meander": "A bend in a river.",
      "River cliff": "Steep outer bank of a meander eroded by the river.",
      "Slip-off slope": "Gentle inner bank of a meander formed by deposition.",
      "Ox-bow lake": "A U-shaped lake formed when a meander is cut off.",
      "Floodplain": "Flat land beside a river that floods.",
      "Levee": "Raised banks along a river, formed naturally or built artificially.",
      "Delta": "Land formed at a river mouth by deposition.",
      "Waterfall": "Where a river flows over a vertical drop.",
      "Gorge": "A steep-sided valley formed by waterfall retreat.",
      "Flood hydrograph": "A graph showing a river's discharge after rainfall.",
      "Discharge": "The volume of water flowing in a river per second.",
      "Hard engineering": "Man-made structures to control rivers.",
      "Soft engineering": "Using natural methods to reduce flood risk.",
      "Dam": "A barrier built across a river to control water flow.",
      "Flood management": "Strategies to reduce flooding risk and damage.",

      // IGCSE Changing Coastal Environments - manually curated accurate definitions
      "Coast": "Where the land meets the sea.",
      "Wave": "Movement of water caused by wind.",
      "Fetch": "The distance wind blows across water to create waves.",
      "Constructive wave": "A wave that builds up beaches by deposition.",
      "Destructive wave": "A wave that erodes beaches and cliffs.",
      "Swash": "Water moving up the beach after a wave breaks.",
      "Backwash": "Water moving down the beach after a wave.",
      "Marine erosion": "Wearing away of land by sea action.",
      "Corrasion (abrasion)": "Sand and pebbles thrown against cliffs by waves.",
      "Longshore drift": "The movement of material along the coast by waves.",
      "Spit": "A finger of sand/shingle sticking out into the sea.",
      "Bar": "A spit that joins two headlands, cutting off a bay.",
      "Tombolo": "A spit joining mainland to an island.",
      "Headland": "Hard rock sticking out into the sea.",
      "Bay": "Softer rock eroded between headlands.",
      "Cave": "A hollow in a cliff eroded by waves.",
      "Arch": "A cave that has broken through a headland.",
      "Stack": "A tall rock left after an arch collapses.",
      "Stump": "A small rock left after a stack erodes.",
      "Beach": "Sand or shingle between high and low tide.",
      "Sand dune": "A hill of sand formed by wind on the coast.",
      "Salt marsh": "Wetland area formed behind a spit or bar.",
      "Deposition (coastal)": "When the sea loses energy and drops material.",
      "Sea wall": "A concrete wall to protect coasts from erosion.",
      "Groynes": "Barriers built to trap sand on beaches.",
      "Rock armour": "Large rocks placed on a coast to absorb wave energy.",
      "Gabions": "Wire cages filled with rocks to reduce erosion.",
      "Revetments": "Sloping barriers that absorb wave energy.",
      "Beach nourishment": "Adding sand/shingle to widen beaches.",
      "Managed retreat": "Allowing certain areas to flood naturally.",

      // IGCSE Changing Ecosystems - manually curated accurate definitions
      "Ecosystem": "A community of plants, animals, and their environment.",
      "Biome": "A large ecosystem with a specific climate and vegetation.",
      "Tropical rainforest": "Hot, wet biome near the equator.",
      "Savanna": "Tropical grassland with scattered trees.",
      "Desert": "Very dry biome with little rainfall.",
      "Temperate forest": "Forest biome with moderate climate.",
      "Tundra": "Cold biome with little vegetation.",
      "Biodiversity": "The variety of species in an ecosystem.",
      "Adaptation": "A feature that helps organisms survive.",
      "Food chain": "Sequence of who eats who.",
      "Food web": "A network of linked food chains.",
      "Producer": "A plant that makes its own food by photosynthesis.",
      "Consumer": "An animal that eats plants or other animals.",
      "Decomposer": "An organism that breaks down dead matter.",
      "Nutrient cycle": "How nutrients move through an ecosystem.",
      "Emergent layer": "The tallest trees in a rainforest.",
      "Canopy": "The thick layer of trees blocking sunlight in a rainforest.",
      "Understorey": "Small trees and shrubs below the canopy.",
      "Shrub layer": "The dark forest floor with little light.",
      "Deforestation": "The clearing of forests.",
      "Desertification": "Land becoming drier and more desert-like.",
      "Sustainable management": "Using resources carefully for the future.",
      "Ecotourism": "Tourism that protects the environment.",
      "Conservation": "Protecting species and ecosystems.",
      "Slash and burn": "Clearing land by cutting and burning vegetation.",
      "Soil erosion": "The removal of soil by wind or water.",
      "Leaching": "Nutrients washed out of soil by heavy rainfall.",
      "Succession": "The natural change in plant and animal communities.",
      "Overgrazing": "Too many animals eating plants, damaging land.",

      // IGCSE Tectonic Hazards - manually curated accurate definitions
      "Plate tectonics": "The theory that Earth's crust is divided into moving plates.",
      "Crust": "The Earth's thin outer layer.",
      "Mantle": "The thick layer under the crust made of molten rock.",
      "Core": "The hot centre of the Earth (inner and outer).",
      "Convection currents": "Heat-driven movements in the mantle moving plates.",
      "Plate boundary": "Where two plates meet.",
      "Constructive boundary": "Where plates move apart.",
      "Destructive boundary": "Where plates collide and subduction occurs.",
      "Collision boundary": "Where two continental plates meet and form mountains.",
      "Conservative boundary": "Where plates slide past each other.",
      "Subduction zone": "Where one plate sinks under another.",
      "Ocean trench": "A deep valley in the ocean formed by subduction.",
      "Fold mountains": "Mountains formed when plates collide.",
      "Earthquake": "A sudden ground shaking caused by plate movement.",
      "Focus": "The point inside Earth where an earthquake starts.",
      "Epicentre": "The point on the surface directly above the focus.",
      "Seismic waves": "Waves of energy released during an earthquake.",
      "Richter scale": "A scale measuring earthquake magnitude.",
      "Mercalli scale": "A scale describing earthquake effects.",
      "Moment magnitude scale": "Modern scale for earthquake strength.",
      "Tsunami": "A large wave caused by an underwater earthquake.",
      "Volcano": "An opening where magma, ash, and gases escape.",
      "Magma": "Molten rock beneath the Earth's surface.",
      "Lava": "Molten rock on the Earth's surface.",
      "Shield volcano": "A wide, gentle volcano formed by lava flows.",
      "Composite volcano": "A steep-sided volcano made of layers of lava and ash.",
      "Supervolcano": "A huge volcano capable of massive eruptions.",
      "Pyroclastic flow": "Fast-moving hot gas, ash, and rocks from a volcano.",
      "Ash cloud": "A cloud of ash from a volcanic eruption.",
      "Prediction": "Trying to forecast hazards.",
      "Preparation": "Planning to reduce hazard impacts.",
      "Protection": "Ways to defend against hazards, like earthquake-proof buildings.",

      // IGCSE Climate Change - manually curated accurate definitions
      "Climate": "The average weather conditions of a place over many years.",
      "Weather": "Day-to-day conditions of the atmosphere.",
      "Climate change": "A long-term change in Earth's climate patterns.",
      "Global warming": "A rise in Earth's average temperatures.",
      "Greenhouse gases": "Gases (e.g. CO₂, methane) that trap heat in the atmosphere.",
      "Carbon dioxide (CO₂)": "A gas released by burning fossil fuels.",
      "Methane": "A powerful greenhouse gas produced by animals, rice farming, and landfills.",
      "Enhanced greenhouse effect": "Extra warming caused by human activity.",
      "Fossil fuels": "Coal, oil, and gas, non-renewable energy sources.",
      "Renewable energy": "Energy that does not run out (e.g. solar, wind).",
      "Non-renewable energy": "Energy that will eventually run out.",
      "Carbon footprint": "The total greenhouse gases caused by a person, company, or country.",
      "Mitigation": "Actions to reduce the causes of climate change.",
      "Adaptation": "Actions to live with the effects of climate change.",
      "Sea level rise": "Rising ocean levels due to melting ice and thermal expansion.",
      "Thermal expansion": "Water expanding as it warms, raising sea levels.",
      "Extreme weather": "Unusual or severe weather events (storms, droughts).",
      "Glacial retreat": "Glaciers shrinking due to melting ice.",
      "Ocean acidification": "Oceans becoming more acidic due to CO₂.",
      "Ecosystem loss": "Habitats destroyed by climate change.",
      "Carbon capture": "Storing CO₂ underground to reduce emissions.",
      "International agreement": "Countries working together to reduce emissions (e.g. Paris Agreement).",

      // IGCSE Changing Populations - manually curated accurate definitions
      "Population": "The total number of people in an area.",
      "Population density": "Number of people per square kilometre.",
      "Birth rate": "Number of live births per 1,000 people per year.",
      "Death rate": "Number of deaths per 1,000 people per year.",
      "Natural increase": "Difference between birth rate and death rate.",
      "Fertility rate": "Average number of children per woman.",
      "Infant mortality": "Number of babies dying before age one per 1,000 births.",
      "Life expectancy": "The average age a person is expected to live.",
      "Ageing population": "When many people are over 65.",
      "Youthful population": "When many people are under 15.",
      "Population pyramid": "A graph showing the age and sex of a population.",
      "Demographics": "Statistics about a population's structure.",
      "Migration": "Movement of people from one place to another.",
      "Immigration": "People moving into a country.",
      "Emigration": "People leaving a country.",
      "Push factors": "Reasons people leave an area (e.g. poverty, war).",
      "Pull factors": "Reasons people move to a new place (e.g. jobs, safety).",
      "Rural–urban migration": "Movement from countryside to cities.",
      "Urbanisation": "Growth of towns and cities.",
      "Overpopulation": "Too many people for resources to support.",
      "Underpopulation": "Too few people to fully use resources.",
      "Population policy": "Government rules to control population growth.",
      "Pro-natalist policy": "Policy encouraging more births.",
      "Anti-natalist policy": "Policy aiming to reduce births.",
      "One-Child Policy": "A former Chinese law limiting most families to one child.",

      // IGCSE Changing Towns and Cities - manually curated accurate definitions
      "Urban area": "A built-up area like a town or city.",
      "Rural area": "Countryside with villages or farms.",
      "Megacity": "A city with more than 10 million people.",
      "CBD (Central Business District)": "The commercial centre of a city.",
      "Urban sprawl": "Spread of cities into the countryside.",
      "Informal settlement": "Housing built without permission, often poor quality.",
      "Shanty town": "Poor-quality housing built quickly by migrants.",
      "Squatter settlement": "Housing built illegally on unused land.",
      "Redevelopment": "Rebuilding old or damaged areas.",
      "Regeneration": "Improving run-down urban areas with new investment.",
      "Urban planning": "Designing and organising a city.",
      "Infrastructure": "Basic services such as roads, power, and water.",
      "Public transport": "Buses, trains, or other shared transport systems.",
      "Greenbelt": "Protected land around a city to stop sprawl.",
      "Sustainable city": "A city designed to protect the environment.",
      "Congestion": "Overcrowding and traffic problems in cities.",
      "Pollution": "Harmful substances in air, water, or land.",
      "Waste management": "How rubbish is collected, treated, and recycled.",
      "Urban heat island": "When cities are warmer than nearby rural areas.",
      "Deindustrialisation": "Decline of manufacturing in cities.",
      "Tertiary industry": "Service-based jobs such as shops, schools, or banks.",
      "Gentrification": "Wealthier people moving into poorer city areas and improving them.",

      // IGCSE Development - manually curated accurate definitions
      "Development": "The progress of a country in wealth, health, and education.",
      "Standard of living": "The level of wealth and comfort available to people.",
      "Quality of life": "People's wellbeing, including health, happiness, and safety.",
      "GDP (Gross Domestic Product)": "The value of goods and services made in a country per year.",
      "GNI (Gross National Income)": "A country's income, including money from abroad.",
      "HDI (Human Development Index)": "A measure combining life expectancy, education, and income.",
      "Literacy rate": "The percentage of adults who can read and write.",
      "Inequality": "When wealth or opportunities are not shared fairly.",
      "Aid": "Help given to a country (money, food, or medicine).",
      "Bilateral aid": "Aid from one country to another.",
      "Multilateral aid": "Aid through international organisations (e.g. UN).",
      "Voluntary aid": "Aid from charities and NGOs.",
      "Trade": "Buying and selling of goods and services.",
      "Fair trade": "Ensuring producers get a fair price for goods.",
      "Sustainable development": "Development that protects resources for the future.",
      "Industrialisation": "Growth of factories and industries.",
      "Globalisation": "The world becoming more connected through trade and culture.",
      "North–South divide": "The global gap between rich and poor countries.",
      "Resource curse": "When countries with resources (like oil) remain poor.",

      // IGCSE Changing Economies - manually curated accurate definitions
      "Primary industry": "Jobs using natural resources (farming, fishing, mining).",
      "Secondary industry": "Jobs making goods in factories.",
      "Quaternary industry": "Jobs in research, IT, and science.",
      "Formal economy": "Legal jobs recorded and taxed by the government.",
      "Informal economy": "Unofficial jobs, often street selling or casual work.",
      "Import": "Goods brought into a country.",
      "Export": "Goods sold to another country.",
      "Tariff": "A tax on imported goods.",
      "Foreign Direct Investment (FDI)": "When companies from one country invest in another.",
      "Transnational Corporation (TNC)": "A company operating in many countries.",
      "Outsourcing": "Moving jobs to other countries for cheaper costs.",
      "Sustainable economy": "An economy that supports people and protects the environment.",

      // IGCSE Resource Provision - manually curated accurate definitions
      "Resource": "Something useful to people, like food, water, or energy.",
      "Resource management": "How resources are controlled and used.",
      "Food security": "Having enough safe and nutritious food.",
      "Water security": "Having reliable access to safe water.",
      "Energy security": "Having reliable and affordable energy.",
      "Overconsumption": "Using too many resources too quickly.",
      "Sustainable resource use": "Using resources carefully to protect the future.",
      "Food miles": "The distance food travels from farm to consumer.",
      "Hydroelectric power": "Electricity made by moving water turning turbines.",
      "Nuclear energy": "Energy made by splitting atoms.",
      "Recycling": "Reusing materials to reduce waste.",
      "Water transfer": "Moving water from one area to another.",
      "Desalination": "Removing salt from seawater to make it drinkable.",

      // IGCSE Cartographic Skills - manually curated accurate definitions
      "Map": "A drawing of part of the Earth's surface.",
      "Atlas": "A book of maps.",
      "Ordnance Survey map": "A detailed map of the UK showing human and physical features.",
      "Scale": "The ratio of map distance to real-life distance.",
      "Grid reference": "Numbers used to locate places on a map.",
      "4-figure grid reference": "Identifies a grid square.",
      "6-figure grid reference": "Pinpoints an exact location.",
      "Contour line": "A line showing areas of equal height.",
      "Spot height": "A point on a map showing exact height.",
      "Layer shading": "Colours showing relief or height.",
      "Compass rose": "Shows direction on a map.",
      "Northing": "Map lines running east to west, measured northwards.",
      "Easting": "Map lines running north to south, measured eastwards.",
      "Longitude": "Imaginary lines running north–south around the globe.",
      "Latitude": "Imaginary lines running east–west around the globe.",
      "Equator": "Line at 0° latitude dividing Earth into north and south.",
      "Prime Meridian": "Line at 0° longitude through Greenwich, London.",
      "Topographic map": "A map showing natural and man-made features.",
      "Choropleth map": "A map using colours/shading to show data.",
      "Flow map": "A map using arrows to show movement.",
      "Isoline map": "A map using lines to join places of equal value.",
      "Climate graph": "A graph showing rainfall and temperature.",
      "Sketch map": "A simple outline map showing main features.",
      "Cross-section": "A cut-through view of a landscape from a map.",

      // KS3 Energy category - manually curated accurate definitions
      "Energy": "Power expended or capable of being transformed into work.",
      "Electricity": "A form of energy resulting from the existence of charged particles (such as electrons or protons), either statically as an accumulation of charge or dynamically as a current.",
      "Generate": "To produce or create.",
      "Coal": "A combustible black or dark brown rock consisting chiefly of carbonized plant matter, found mainly in underground seams and used as fuel.",
      "Wave energy": "Electrical energy generated by harnessing the up-and-down motion of ocean waves. Wave power is typically produced by floating turbine platforms or buoys that rise and fall with the swells.",
      "Solar energy": "Energy from the sun. It's the most abundant energy resource on Earth and can be converted from radiant energy from the sun to electrical or thermal energy.",
      "Solar": "Energy from the sun. It's the most abundant energy resource on Earth and can be converted from radiant energy from the sun to electrical or thermal energy.",
      "Nuclear energy": "The energy in the nucleus, or core, of an atom. Nuclear power is the practice of splitting atoms to boil water, turn turbines and generate electricity.",
      "Nuclear": "The energy in the nucleus, or core, of an atom. Nuclear power is the practice of splitting atoms to boil water, turn turbines and generate electricity.",
      "Wind energy": "Energy obtained from harnessing the wind with wind turbines. Wind can be strong enough to move a fan or propeller blades and convert the movement to electrical energy.",
      "Wind": "Energy obtained from harnessing the wind with wind turbines. Wind can be strong enough to move a fan or propeller blades and convert the movement to electrical energy.",
      "Natural gas": "Widely used for cooking and for heating homes. It consists mostly of methane and is found near oil deposits below Earth's surface.",
      "Hydroelectricity": "A form of energy that harnesses the power of water in motion—such as water flowing over a waterfall—to generate electricity.",
      "Hydroelectric": "A form of energy that harnesses the power of water in motion—such as water flowing over a waterfall—to generate electricity.",
      "Biomass": "Organic material from plants or animals that has absorbed energy from the Sun. This energy is released as heat when burned.",
      "Geothermal": "When heat is harvested from below the Earth's surface to heat water that then converts to steam to turn a turbine and create electricity.",
      "Oil": "The remains of plants and animals that have been exposed to thousands of years of heat and pressure, creating a carbon-rich liquid used to produce energy and materials.",
      "Fossil fuels": "Fuels found in Earth's crust that contain carbon and hydrogen, which can be burned for energy. Examples: coal, oil, natural gas. They are non-renewable.",
      "Renewable": "A resource that is not depleted when used and can continue to be used for the foreseeable future.",
      "Alternative": "A different available possibility, most commonly used when describing a fuel choice that is not a fossil fuel.",
      "Energy flow": "The movement of different types of energy around the world. Example: oil exports from the Middle East to other regions.",
      "Industry": "A sector of a country's economy, normally referring to production sectors such as energy and factories.",
      "Transportation": "The movement of people or resources across the Earth, typically by car, rail, plane or ship.",
      "Transport": "The movement of people or resources across the Earth, typically by car, rail, plane or ship.",
      "Commercial": "The industry of buying and selling products.",
      "Residential": "Anything in relation to people and where they live.",
      "Consumption": "To use a resource.",
      "Sustainability": "Meeting the needs of the present without sacrificing the resources of tomorrow.",
      "Sustainable": "Meeting the needs of the present without sacrificing the resources of tomorrow.",
      "CO₂": "A colourless, odourless gas produced by burning carbon and organic compounds and by respiration. It is naturally present in air.",
      "Mining": "The process or industry of obtaining coal or other minerals from the Earth's surface.",
      "Carbon": "A chemical element that exists in all plants and animals, and is an important part of coal and oil. When burned it produces carbon dioxide and carbon monoxide.",
      "Carbon footprint": "A measure of the amount of carbon dioxide and other carbon compounds emitted due to the consumption of fossil fuels by a particular person, group, etc.",
      "MEDC": "More Economically Developed Country.",
      "LEDC": "Less Economically Developed Country.",
      "Energy efficient": "Reducing the amount of energy consumed to complete a task or power a product.",
      "Personal action": "When an individual takes steps to reduce their impact.",
      "Recycle": "To break something down and create something new from its parts.",

      // KS3 Middle East category - manually curated accurate definitions
      "Region": "An area of the world with similar features.",
      "Transcontinental": "A country that is in more than one continent.",
      "Human characteristics": "Features made by people, like cities or religion.",
      "Physical characteristics": "Natural features like rivers, mountains, and deserts.",
      "Christianity": "A world religion based on the teachings of Jesus Christ.",
      "Islam": "A world religion based on the teachings of the Prophet Muhammad.",
      "Judaism": "A world religion based on Jewish beliefs and traditions.",
      "Desert": "A dry area with little rain and little vegetation.",
      "Gulf": "A large area of sea partly surrounded by land.",
      "Climate": "The usual weather of a place over many years.",
      "Climate graph": "A graph that shows temperature and rainfall in a place.",
      "Arid": "Very dry with little rain.",
      "Biome": "A large area with similar climate, plants, and animals.",
      "Vegetation": "Plants in an area.",
      "Adaptation": "A feature that helps plants or animals survive.",
      "Nocturnal": "Animals that are active at night.",
      "Crude oil": "Oil taken from the ground before it is processed.",
      "Oil": "A liquid fuel made from crude oil.",
      "Oil reserve": "Stored underground supplies of oil.",
      "Development": "How a country improves in wealth, health, and education.",
      "HDI": "Human Development Index; a measure of life expectancy, education, and income.",
      "Life expectancy": "The average age people are expected to live.",
      "Education": "Learning in schools, universities, or training.",
      "Income": "The money people earn.",
      "Trade": "Buying and selling goods or services.",
      "Import": "Goods brought into a country.",
      "Export": "Goods sold to another country.",
      "Dam": "A wall built across a river to store water.",
      "Hydroelectric": "Electricity made by water turning turbines.",
      "Sustainable": "Using resources in a way that protects the future.",
      "Socially sustainable": "Helping people and communities now and in the future.",
      "Economically sustainable": "Helping jobs and money grow without problems in the future.",
      "Environmentally sustainable": "Protecting nature and the planet while using resources.",
      "Culture": "Beliefs, traditions, and ways of life of a group of people.",
      "Human rights": "The basic rights and freedoms all people should have.",
      "Conflict": "Disagreement or fighting between groups or countries.",
      "Refugee": "A person forced to leave their country to stay safe.",
      "Migration": "When people move from one place to another.",
      "OPEC": "Organisation of Petroleum Exporting Countries; a group that controls much of the world's oil supply.",
      "Pipeline": "Large pipes used to carry oil or gas.",
      "Desalination": "The process of removing salt from seawater to make it drinkable.",
      "Nomad": "A person who moves from place to place rather than living in one home.",
      "Urbanisation": "When more people move to cities.",
      "Tourism": "When people travel to visit places for holidays.",
      "Water scarcity": "When there is not enough safe water for people's needs.",

      // KS3 Oceans category - manually curated accurate definitions
      "Ocean": "A very large body of salt water that covers most of the Earth.",
      "Salinity": "How salty the water is.",
      "Current": "The movement of water in the ocean, like a river in the sea.",
      "Tide": "The rise and fall of the sea, caused by the moon and sun.",
      "Ecosystem": "Living things and their environment working together.",
      "Coral Reef": "A large group of corals, fish, and plants living together in the sea.",
      "Algae": "Simple water plants, like seaweed.",
      "Plankton": "Very tiny plants and animals that float in the water.",
      "Marine": "Anything related to the sea.",
      "Aquatic": "Living in or near water.",
      "Biodiversity": "The variety of plants and animals in an area.",
      "Habitat": "The natural home of a plant or animal.",
      "Pollution": "Harmful materials in the environment, like plastic or oil spills.",
      "Conservation": "Protecting nature and the environment.",
      "Sustainability": "Using resources without harming them for the future.",
      "Erosion": "When waves wear away rocks and land.",
      "Overfishing": "Catching too many fish so the fish population cannot recover.",
      "Endangered Species": "Animals or plants at risk of disappearing forever.",
      "Marine Debris": "Rubbish, like plastic, left in the sea.",
      "Food Chain": "A diagram that shows who eats who in nature.",
      "Photosynthesis": "The way plants make food using sunlight, water, and air.",
      "Hydrosphere": "All the water on Earth (oceans, rivers, lakes, ice).",
      "Sonar": "Technology that uses sound waves to measure the sea or find objects underwater.",
      "Flora and Fauna": "Plants (flora) and animals (fauna) in an environment.",
      "Eco-friendly": "Something that does not harm the environment.",
      "Oceanography": "The study of the ocean.",
      "Climate Change": "The long-term change in the Earth's temperature and weather patterns.",
      "Coral Bleaching": "When corals lose their colour and health because of warmer water.",
      "Acidification": "When the ocean becomes more acidic because of too much carbon dioxide.",
      "Predator": "An animal that hunts and eats other animals.",

      // KS3 Changing China category - manually curated accurate definitions
      "Urbanisation": "When more people move from the countryside to live in towns and cities.",
      "Rural": "Countryside areas with farms and small villages.",
      "Population Density": "The number of people living in one area (e.g. per km²).",
      "Migration": "When people move from one place to another.",
      "Economic Growth": "When a country's economy becomes bigger and richer.",
      "Infrastructure": "Basic services like roads, railways, water, and electricity.",
      "GDP (Gross Domestic Product)": "The total value of all goods and services a country makes in one year.",
      "Industrialization": "When a country develops more factories and industries.",
      "One-Child Policy": "A Chinese law (1979–2015) that limited most families to one child.",
      "Three Gorges Dam": "A huge dam on the Yangtze River that makes hydroelectric power.",
      "Pollution": "Harmful materials in the air, water, or land.",
      "Renewable Energy": "Energy from sources that don't run out, like wind or solar.",
      "Sustainable Development": "Improving life today without harming resources for the future.",
      "Biodiversity": "The variety of plants and animals in an area.",
      "Conservation": "Protecting the environment and animals.",
      "Ecotourism": "Tourism that protects nature and helps local people.",
      "Manufacturing": "Making products in factories.",
      "Export": "Goods sold to another country.",
      "Import": "Goods bought from another country.",
      "Tariff": "A tax on goods coming into a country.",
      "Globalisation": "The growing connection between countries through trade, ideas, and culture.",
      "Labor Force": "All the people who are able to work in a country.",
      "Demographics": "Data about a population, such as age, gender, and income.",
      "Urban Planning": "Designing and organising towns and cities.",
      "Cultural Revolution": "A political movement in China (1966–1976) led by Mao Zedong.",
      "Han Chinese": "The largest ethnic group in China.",
      "Tibet": "A region in western China with its own culture and history.",
      "Special Economic Zones (SEZs)": "Areas in China where businesses get tax breaks and attract foreign investment.",
      "Fertility Rate": "The average number of children a woman is expected to have.",
      "Life Expectancy": "The average age people are expected to live.",
      "Literacy Rate": "The percentage of people who can read and write.",
      "Income Inequality": "When some people earn much more money than others.",
      "Technological Innovation": "New inventions and technology that improve life and business.",
      "Panda Conservation": "Efforts in China to protect pandas and their habitats.",
      "Air Quality": "A measure of how clean or polluted the air is.",

      // KS3 Africa category - manually curated accurate definitions
      "Sahara Desert": "The largest hot desert in the world, in North Africa.",
      "Savanna": "Grassland with scattered trees, found in Africa.",
      "Sub-Saharan Africa": "The part of Africa south of the Sahara Desert.",
      "Urbanisation": "When more people move from the countryside to towns and cities.",
      "Population Density": "The number of people living in one area (e.g. per km²).",
      "Desertification": "When land becomes drier and turns into desert.",
      "Equator": "An imaginary line around the middle of the Earth.",
      "Tropical Climate": "Hot and wet weather near the equator.",
      "Migration": "When people move from one place to another.",
      "Colonialism": "When one country takes control of another.",
      "Independence": "When a country rules itself and is no longer controlled by another.",
      "Biodiversity": "The variety of plants and animals in an area.",
      "Great Rift Valley": "A large valley in East Africa formed by tectonic movement.",
      "Rainforest": "A hot, wet forest found near the equator.",
      "Kalahari Desert": "A desert in southern Africa.",
      "Nile River": "The longest river in Africa, flowing through Egypt and other countries.",
      "Lake Victoria": "The largest lake in Africa, found in East Africa.",
      "Famine": "A serious lack of food that causes hunger and death.",
      "Resource Curse": "When a country with many natural resources still stays poor or has problems.",
      "Mount Kilimanjaro": "Africa's highest mountain, in Tanzania.",
      "Atlas Mountains": "A mountain range in North Africa.",
      "Sustainable Development": "Improving life now without harming resources for the future.",
      "GDP (Gross Domestic Product)": "The total value of all goods and services a country makes in one year.",
      "Literacy Rate": "The percentage of people who can read and write.",
      "Deforestation": "Cutting down large areas of forest.",
      "Hydroelectric Power": "Electricity made by moving water turning turbines.",
      "Cape of Good Hope": "A rocky headland on the coast of South Africa.",
      "Congo Basin": "A large rainforest area around the Congo River.",
      "Demographics": "Data about a population, such as age, gender, and income.",
      "Apartheid": "A system in South Africa where people were separated by race (1948–1994).",
      "Slavery": "Forcing people to work without pay or freedom.",
      "Imperialism": "When countries expand their power and control over others.",
      "Scramble for Africa": "When European countries divided Africa into colonies in the 1800s.",
      "Trans-Saharan Trade Routes": "Ancient routes across the Sahara Desert used for trade.",

      // KS3 Development category - manually curated accurate definitions
      "Development": "The process of a country or place becoming better, usually through improvements in wealth, health, and education.",
      "Standard of living": "The level of wealth, comfort, and goods available to people in a country.",
      "Quality of life": "How happy and comfortable people feel in their daily lives, including health, safety, and education.",
      "GDP (Gross Domestic Product)": "The total value of all goods and services a country makes in one year.",
      "GDP": "The total value of all goods and services a country makes in one year.",
      "GNI (Gross National Income)": "The total money earned by a country's people and businesses, including income from abroad.",
      "GNI": "The total money earned by a country's people and businesses, including income from abroad.",
      "HDI (Human Development Index)": "A measure that combines life expectancy, education, and income to show how developed a country is.",
      "Literacy rate": "The percentage of adults in a country who can read and write.",
      "Life expectancy": "The average age a person can expect to live in a country.",
      "Birth rate": "The number of babies born per 1,000 people in a year.",
      "Death rate": "The number of people who die per 1,000 people in a year.",
      "Infant mortality": "The number of babies who die before the age of one, per 1,000 live births.",
      "Inequality": "When resources, money, or opportunities are not shared fairly between people or groups.",
      "Aid": "Help given from one country to another, such as money, food, or medicine.",
      "Bilateral aid": "Aid given directly from one country to another.",
      "Multilateral aid": "Aid given through an international organisation (like the UN or World Bank).",
      "Voluntary aid": "Aid given by charities and non-governmental organisations (NGOs).",
      "Trade": "The buying and selling of goods and services between countries.",
      "Fair trade": "A system that makes sure producers in poorer countries are paid fairly for their products.",
      "Sustainable development": "Improving life now without damaging the environment or resources for future generations.",
      "Industrialisation": "When a country develops more factories and industries instead of relying mainly on farming.",
      "Urbanisation": "The growth of towns and cities as more people move from the countryside to live in them.",
      "Globalisation": "The growing connection between countries through trade, travel, communication, and culture.",
      "Primary industry": "Jobs that use natural resources, like farming, fishing, and mining.",
      "Secondary industry": "Jobs that involve making things in factories, such as clothes, cars, or food products.",
      "Tertiary industry": "Jobs that provide services, like teachers, doctors, shopkeepers, or bus drivers.",
      "Quaternary industry": "Jobs based on knowledge and research, such as scientists, IT, or design.",
      "MIC (Middle-Income Country)": "A country that is not very poor but not very rich, in between LICs and HICs.",
      "MIC": "A country that is not very poor but not very rich, in between LICs and HICs.",
      "LIC (Low-Income Country)": "A country with low income and often lower levels of health and education.",
      "LIC": "A country with low income and often lower levels of health and education.",
      "HIC (High-Income Country)": "A country with high income, wealth, and high levels of health and education.",
      "HIC": "A country with high income, wealth, and high levels of health and education.",
      "North–South Divide": "A way of showing how wealthier countries are mostly in the north of the world and poorer countries are mostly in the south (though there are exceptions).",
      "Inequality of access": "Differences in how easily people can get healthcare, education, clean water, or technology.",

      // KS3 Coasts category - manually curated accurate definitions
      "Coast": "The area where the land meets the sea.",
      "Coasts": "The area where the land meets the sea.",
      "Beach": "A stretch of sand or pebbles along the coast.",
      "Cliff": "A steep rock face at the edge of the land.",
      "Tide": "The regular rise and fall of the sea caused by the moon and sun.",
      "Wave": "The movement of water in the sea, usually caused by wind.",
      "Destructive wave": "Strong waves that erode (wear away) the coast and carry material away.",
      "Constructive wave": "Gentle waves that build up beaches by dropping sand and pebbles.",
      "Swash": "The water that moves up the beach after a wave breaks.",
      "Backwash": "The water that flows back down the beach into the sea.",
      "Fetch": "The distance the wind blows across the sea to make waves.",
      "Erosion": "The process of wearing away rocks and land by waves.",
      "Deposition": "The dropping of sand, pebbles, or mud carried by the sea.",
      "Hydraulic action": "When waves force water and air into cracks in rocks, breaking them apart.",
      "Attrition": "When rocks and pebbles crash into each other and break into smaller, smoother pieces.",
      "Solution (erosion)": "When rocks are dissolved by sea water.",
      "Abrasion": "When waves throw sand and rocks against cliffs, wearing them away.",
      "Transportation": "The movement of sand, pebbles, and mud by the sea.",
      "Solution (transportation)": "When dissolved materials are carried in the sea water.",
      "Saltation": "When small pebbles or sand bounce along the sea bed.",
      "Suspension": "When light materials like sand or mud are carried and float in the water.",
      "Traction": "When large rocks roll along the sea bed.",
      "Headland": "A piece of land that sticks out into the sea.",
      "Bay": "A curved area of coast between two headlands, often with a beach.",
      "Fault (crack)": "A weakness or break in the rock.",
      "Cave": "A hollow space in a cliff made by waves.",
      "Arch": "A cave that has broken through to the other side of a headland.",
      "Stack": "A tall pillar of rock left after an arch collapses.",
      "Stump": "A small, low piece of rock left after a stack is worn down.",
      "Longshore drift": "The movement of sand and pebbles along the coast, caused by waves hitting at an angle.",
      "Spit": "A narrow stretch of sand or shingle sticking out into the sea.",
      "Bar": "A spit that joins two headlands and blocks a bay.",
      "Tombolo": "A spit that connects the mainland to an island.",
      "Groynes": "Wooden or stone barriers built on beaches to trap sand.",
      "Rock armour": "Large rocks placed along the coast to absorb wave energy.",
      "Revetments": "Sloping wooden or concrete walls built on beaches to absorb wave energy.",
      "Beach Nourishment": "Adding sand or shingle to a beach to replace material lost by erosion.",
      "Gabions": "Wire cages filled with rocks, placed along coasts to absorb wave energy.",
      "Sea walls": "Concrete walls built along the coast to protect against waves.",

      // KS3 Tropical Rainforests category - manually curated accurate definitions
      "Rainforest": "A hot, wet forest found near the equator.",
      "Tropical rainforest": "A hot, wet forest found near the equator.",
      "Tropical": "Hot and wet all year round, near the equator.",
      "Climate": "The usual weather of a place over many years.",
      "Weather": "The condition of the air at one time, like rain, sun, wind, or temperature.",
      "Equator": "An imaginary line around the middle of the Earth.",
      "Tropic of Cancer": "An imaginary line north of the equator.",
      "Tropic of Capricorn": "An imaginary line south of the equator.",
      "Climate graph": "A graph that shows temperature and rainfall in a place.",
      "Shrub Layer": "The bottom layer of the rainforest with small plants.",
      "Under Canopy": "The layer of small trees and young plants under the canopy.",
      "Canopy": "The thick layer of trees whose branches and leaves block most sunlight.",
      "Emergent layer": "The tallest trees that rise above the canopy.",
      "Buttress roots": "Large roots that support tall trees.",
      "Drip tip leaves": "Leaves with pointed ends so water can run off quickly.",
      "Lianas": "Thick climbing plants that use trees for support.",
      "Epiphytes": "Plants that grow on other plants to reach sunlight.",
      "Strangler fig": "A plant that grows around a tree and can kill it.",
      "Adaptation": "A special feature that helps a plant or animal survive.",
      "Behavioral": "An action or way an animal behaves that helps it survive.",
      "Structural": "A body part or feature of a plant or animal that helps it survive.",
      "Tribe": "A group of people, often living traditionally in the rainforest.",
      "Deforestation": "Cutting down large areas of forest.",
      "Logging": "Cutting down trees for wood.",
      "Hydroelectric power": "Electricity made by water turning turbines in a dam.",
      "Extinct": "When a type of animal or plant no longer exists.",
      "Global warming": "The Earth getting hotter because of more greenhouse gases.",
      "Sustainable": "Using resources in a way that does not damage them for the future.",
      "Ecotourism": "Tourism that protects the environment and helps local people.",
      "Slash and burn": "Cutting and burning trees to clear land for farming.",

      // KS3 Rivers category - manually curated accurate definitions
      "River": "A large stream of flowing water.",
      "Channel": "The path a river flows in.",
      "Long profile": "A side view of the river from source to mouth.",
      "Cross profile": "A cut-through view of the river showing its shape.",
      "Source": "The start of a river.",
      "Mouth": "The place where a river meets the sea, lake, or ocean.",
      "Upper course": "The start of the river, near the source.",
      "Middle course": "The middle part of the river.",
      "Lower course": "The last part of the river before the mouth.",
      "Erosion": "The wearing away of land by water.",
      "Deposition": "When a river drops sand, mud, or pebbles it was carrying.",
      "Transportation": "The movement of material by the river.",
      "Abrasion": "When rocks scrape along the river bed and banks, wearing them away.",
      "Attrition": "When rocks crash into each other and break into smaller, smoother pieces.",
      "Solution (erosion)": "When rocks dissolve in the river water.",
      "Hydraulic action": "When fast water forces air and water into cracks, breaking rock.",
      "Traction": "When large rocks roll along the river bed.",
      "Saltation": "When small pebbles bounce along the river bed.",
      "Suspension": "When light material like sand or silt floats in the water.",
      "Solution (transportation)": "When dissolved material is carried in the river water.",
      "River basin": "The area of land drained by a river and its tributaries.",
      "Watershed": "The high land dividing one river basin from another.",
      "Tributary": "A smaller river that joins a bigger one.",
      "Meander": "A bend in a river.",
      "Confluence": "The place where two rivers join.",
      "Valley": "A low area of land with hills or mountains on each side, often with a river.",
      "Waterfall": "When a river flows over a steep drop.",
      "Gorge": "A deep, narrow valley with steep sides, often made by a waterfall retreating.",
      "Ox-bow lake": "A U-shaped lake formed when a meander is cut off.",
      "Rapids": "Fast, rough water in a river.",
      "River cliff": "The steep side of a meander made by erosion.",
      "Undercutting": "When water erodes the bottom of a river bank, making it collapse.",
      "Levee": "Raised banks of sand and mud along a river.",
      "River beach (slip-off slope)": "A gently sloping area of sand or mud inside a meander.",
      "Estuary": "The wide river mouth where it meets the sea and is affected by tides.",
      "Delta": "Land formed at a river mouth by deposited material, often shaped like a triangle.",
      "Interlocking spurs": "Hills that stick out into the river's path in the upper course.",
      "V-shaped valley": "A steep valley shaped like a \"V\", made by vertical erosion.",
      "Flood plain": "Flat land beside a river that floods easily.",
      "Evaporation": "When water changes into water vapour (gas) and rises.",
      "Transpiration": "When plants release water vapour from their leaves.",
      "Precipitation": "Rain, snow, sleet, or hail falling from the sky.",
      "Surface runoff": "Rainwater flowing over the land into rivers.",
      "Groundwater": "Water stored underground in rocks or soil.",
      "Lake": "A large area of water surrounded by land.",
      "Stream": "A small, narrow river.",
      "Ocean": "A very large area of salt water.",
      "Throughflow": "Water moving sideways through soil downhill.",
      "Percolation": "Water moving down through rocks and soil.",
      "Streamflow": "Water flowing in a stream or river.",
      "Stemflow": "Rainwater running down the stems of plants and trees.",
      "Vegetation": "Plants that cover the land.",
      "Spring": "A place where water flows naturally from the ground.",
      "Water store": "A place where water is kept, like lakes, ice, soil, or groundwater.",
      "Infiltration": "When water soaks into the ground.",
      "Flood": "When a river overflows and water covers the land.",
      "Permeable": "Rock or soil that lets water pass through it.",
      "Impermeable": "Rock or soil that does not let water pass through it.",
      "Saturated": "When the ground is full of water and cannot hold more.",
      "Urban": "An area that is a town or city.",

      // KS3 Map Skills category - manually curated accurate definitions
      "Ordnance Survey": "The national mapping agency for Great Britain. The maps they produce are ideal for planning walking expeditions.",
      "Atlas": "A book that contains collections of maps and useful geographical information normally about the entire planet.",
      "Satellite Navigation": "A digital system that uses satellites to pinpoint a person's location on the planet that is then represented as a point on a digital map.",
      "S.T.A.C.K": "The essentials expected to be on a basic map: Scale, Title, Accurate, Compass, Key.",
      "Scale": "A way of representing the actual size of a feature on a map. e.g. 1:25,000 would mean that every cm you measure on a map would be 25,000 cm in reality.",
      "Title": "The words given to describe a map. The title is normally a description of the real world location being represented.",
      "Accurate": "A map must be free from error and a true representation of the actual location.",
      "4 Point Compass": "The four main points of the compass are north, east, south and west.",
      "8 Point Compass": "4 additional points are added to the basic compass. These points are situated between the main points, and always start with north or south. E.g. between north and east is north-east.",
      "Key": "A section dedicated to understanding the map, the symbols used on the map and other more complex features.",
      "Spot Height": "A point on a map that is labelled with its height above sea level, normally in meters.",
      "Contour lines": "Thin brown lines drawn on OS maps that connect points of the same height. Normally the height of the line is also written on it at some point. These are great at showing the relief of the land.",
      "Layer Shading": "When sections of the same height on a map are shaded the same colour. Maps normally become darker the higher they get.",
      "Relief": "The physical shape of the land. Normally described with words like Steep, Flat, Valley, Hill, Mountain.",
      "Eastings": "Vertical lines that divide a map into sections running from west to east. These are the first numbers you find when calculating a grid reference.",
      "Northings": "Horizontal lines that divide a map into sections from south to north. These are the second number you find when calculating grid references.",
      "4 Figure Grid Reference": "A combination of eastings and northings to give 4 numbers. These are used to find sections on the map, e.g. the first 2 digits are eastings, the 3rd and 4th are northings.",
      "6 Figure Grid Reference": "More detailed than a 4-figure grid reference. Designed to help find a specific location within a grid rather than just a general square.",
      "Equator": "An imaginary line that runs exactly half way between the north and south poles.",
      "Latitude": "Imaginary lines around the Earth running parallel to the equator. These are measured in degrees north or south of the equator.",
      "Longitude": "Imaginary lines around the Earth running from the north to the south pole. These are measured in degrees east or west of the Prime Meridian.",

      // KS3 Plate Tectonics category - manually curated accurate definitions
      "Magma": "Hot fluid or semifluid material below or within the Earth's crust from which lava is formed.",
      "Molten": "Made liquid by heat; melted.",
      "Lava": "Rock that in its molten form (as magma) erupts from volcanoes; lava is what magma is called when it reaches the surface.",
      "Volcano": "An opening in the Earth's crust through which molten lava, ash, and gases are ejected.",
      "Shield Volcano": "A volcano with long, gentle slopes, built primarily by lava flows.",
      "Cone Volcano": "Built by ejecting material from a volcanic vent, piling up around the vent in the shape of a cone with a central crater.",
      "Composite Cone": "Also called a stratovolcano; a tall, conical volcano built up by many layers of hardened lava, tephra, pumice, and volcanic ash.",
      "Supervolcano": "An enormous volcano far larger than ordinary ones, caused when a huge magma chamber erupts and the land collapses to form a caldera.",
      "Ash": "Powdery material thrown out by a volcano.",
      "Crater": "A bowl-shaped depression at the mouth of a volcano or geyser.",
      "Convection Current": "The movement of fluid due to differences in temperature and density.",
      "Plate": "Large sections of the Earth's surface that move slowly, meeting and diverging to create earthquakes, volcanoes, and mountains.",
      "Crust": "The outer layer of the Earth.",
      "Mantle": "The layer of the Earth between the crust and the core.",
      "Core": "The innermost layer of the Earth, rich in iron and nickel, split into the inner core and outer core.",
      "Constructive Boundary": "Where two plates slide apart, creating features like mid-ocean ridges.",
      "Destructive Boundary": "Where two plates move towards each other, often forming subduction zones.",
      "Subduction Zone": "Where one tectonic plate moves under another, sinking into the mantle, often causing earthquakes.",
      "Ocean Trench": "Long, narrow depressions of the sea floor, usually around subduction zones.",
      "Conservative Boundary": "Where two plates slide alongside each other, either in the same direction at different speeds or in opposite directions.",
      "Collision Boundary": "When two continental plates collide, land is forced upwards to form fold mountains.",
      "Fold Mountain": "Mountain ranges formed when continental plates collide and push land upwards.",
      "Earthquake": "A sudden, violent shaking of the ground caused by movement in the Earth's crust.",
      "Epicentre": "The point on the Earth's surface directly above the earthquake's focus.",
      "Focus": "The place inside the Earth's crust where an earthquake originates.",
      "Tsunami": "A long, high sea wave caused by an earthquake or other disturbance.",
      "Richter Scale": "A numerical scale that measures the magnitude of an earthquake.",
      "Mercalli Scale": "A twelve-point scale describing the intensity of an earthquake.",
      "Moment Magnitude Scale": "A scale that calculates the total energy released by an earthquake, more accurately than the Richter scale.",
      "Seismic Wave": "A wave in the Earth produced by an earthquake.",
      "P Wave": "A primary seismic wave that travels fastest and arrives first at a seismograph.",
      "S Wave": "A secondary seismic wave that shakes the ground side-to-side; usually more destructive.",

      // Common geography terms from original dictionary
      "Physical geography": "The study of the natural world including rivers, coasts, mountains, and climate.",
      "Human geography": "The study of people, cities, jobs, and how humans live and interact with places.",
      "Environmental geography": "The study of how people and the environment affect each other.",
      "Cause": "The reason why something happens or occurs.",
      "Effect": "The result or change that comes from something happening.",
      "Response": "Actions taken in reaction to an event or situation.",
      "Short-term response": "Quick actions taken immediately after a problem or disaster occurs.",
      "Long-term response": "Sustained actions taken over months or years to address a problem.",
      "Primary response": "The very first actions taken immediately after a disaster strikes.",
      "Secondary response": "Follow-up actions focused on recovery and preventing future problems.",
      "Local": "Relating to a small area like a town or village.",
      "National": "Relating to an entire country as a whole.",
      "Global": "Relating to the entire world or planet.",
      "Regional": "Relating to a specific region or area within a country.",
      "Canal": "An artificial waterway constructed for navigation or irrigation.",
      "Weathering": "The breaking down of rocks and minerals through physical and chemical processes.",
      "Water cycle": "The continuous movement of water between the sea, air, and land.",
      "River cycle": "The stages in the development of a river system.",
      "Climate cycle": "Long-term patterns of climate variation.",
      "Weather cycle": "Short-term atmospheric conditions and patterns.",
      "Process": "A series of natural actions or changes, like erosion or deposition.",
      "System": "A set of interconnected components working together.",
      "Opportunities": "The positive benefits or advantages people get from living in a particular location.",
      "Benefits": "Positive outcomes or advantages gained from something.",
      "Advantages": "Favorable circumstances or conditions that provide benefit.",
      "Resources": "Materials or assets that can be used to produce goods or services.",
      "Contamination": "The presence of harmful substances in the environment.",
      "Degradation": "The deterioration or decline in quality of the environment.",
      "Destruction": "The act of causing severe damage or complete ruin.",
      "Hard engineering": "Large artificial structures built to control natural processes, like dams and sea walls.",
      "Soft engineering": "Natural or small-scale solutions that work with nature to manage environmental challenges.",
      "Natural engineering": "Using natural processes and materials to solve environmental problems.",
      "Coastal engineering": "Engineering solutions specifically designed for coastal environments.",
      "Green engineering": "Environmentally sustainable engineering practices.",
      "Renewable": "Resources that can be replenished naturally and used repeatedly.",
      "Alternative": "Different options or choices available instead of the conventional approach.",
      "Secure": "Safe, stable, and protected from harm or loss.",
      "Abiotic": "The non-living components of an environment, such as air, water, and rocks.",
      "Biotic": "The living components of an environment, including plants and animals.",
      "Organic": "Relating to or derived from living organisms.",
      "Natural": "Existing in or caused by nature, not made by humans.",
      "Biological": "Relating to living organisms and their vital processes.",
      "Evolution": "The gradual development and change of species over time.",
      "Modification": "The action of making changes or alterations.",
      "Adjustment": "Small changes made to achieve better fit or performance.",
      "Community": "All the different species living together in the same area.",
      "Environment": "The surroundings and conditions in which organisms live.",
      "Tourism": "Travel undertaken for recreation, leisure, or business purposes.",
      "Recreation": "Activities done for enjoyment and relaxation.",
      "Transport": "The movement of people or goods from one place to another.",
      "Equator": "An imaginary line around the middle of Earth that divides it into Northern and Southern hemispheres.",
      "Prime Meridian": "The line of longitude at 0 degrees, passing through Greenwich, England.",
      "Tropic of Cancer": "The line of latitude 23.5° north of the Equator.",
      "Tropic of Capricorn": "The line of latitude 23.5° south of the Equator.",
      "Arctic Circle": "The line of latitude 66.5° north, near the North Pole.",
      "Antarctic Circle": "The line of latitude 66.5° south, near the South Pole.",
      "International Date Line": "An imaginary line roughly following the 180° meridian, where the date changes.",
      "Afforestation": "The establishment of forests on land that was not previously forested.",
      "Reforestation": "The replanting of trees in areas where forests have been cut down.",
      "Inner core": "The solid, extremely hot center of the Earth.",
      "Outer core": "The liquid layer of molten iron and nickel surrounding Earth's inner core.",
      "Mantle": "The thick layer of hot, dense rock between Earth's crust and core.",
      "Crust": "The thin, solid outermost layer of Earth where we live.",
      "Lithosphere": "Earth's crust and uppermost mantle, broken into moving tectonic plates.",
      "Asthenosphere": "The upper layer of the mantle, partially molten and allowing plate movement.",
      "Hypocenter": "Another term for the focus of an earthquake.",
      "Plate boundary": "The edge where two tectonic plates meet and interact.",
      "Fault line": "A fracture in the Earth's crust where movement has occurred.",
      "Plate margin": "The boundary area between tectonic plates.",
      "Tectonic zone": "An area of tectonic activity with frequent earthquakes or volcanic activity.",
      "Tectonic plate": "Large sections of Earth's crust that move slowly over time.",
      "Continental plate": "Tectonic plates that primarily consist of continental crust.",
      "Oceanic plate": "Tectonic plates that primarily consist of oceanic crust.",
      "Crustal block": "Large sections of the Earth's crust bounded by faults.",
      "Geyser": "A hot spring that periodically erupts water and steam.",
      "Hot spring": "A spring heated by geothermal energy from beneath the Earth's surface.",
      "Earthquake": "Sudden shaking of the ground caused by the movement of tectonic plates.",
      "Tremor": "A slight earthquake or vibration of the ground.",
      "Seismic activity": "Earthquake-related phenomena and ground movements.",
      "Ground motion": "The movement of the Earth's surface during an earthquake.",
      "Global warming": "The long-term increase in Earth's average temperature due to greenhouse gases.",
      "Greenhouse effect": "The trapping of heat in Earth's atmosphere by greenhouse gases.",
      "Temperature rise": "An increase in average temperatures over time.",
      "Weather variation": "Changes in weather conditions over short periods.",
      "Seasonal change": "Regular variations in weather and climate throughout the year.",
      "Population": "The total number of people living in a specific area.",
      "Census": "An official count and survey of a population.",
      "Fertility": "The average number of children born to women of childbearing age.",
      "Mortality": "Death rate in a population.",
      "Population growth": "The increase in population size over time.",
      "Natural increase": "When the birth rate is higher than the death rate in a population.",
      "Demographic transition": "The shift from high birth and death rates to low birth and death rates.",
      "Pro-natalist": "Policies designed to encourage people to have more children.",
      "Anti-natalist": "Policies aimed at reducing birth rates and population growth.",
      "Population policy": "Government strategies to influence population growth.",
      "Family planning": "The practice of controlling family size and spacing of children.",
      "Population control": "Measures taken to limit population growth.",
      "Birth control": "Methods used to prevent pregnancy.",
      "Migrant": "A person who moves from one place to another, often for work or better opportunities.",
      "Immigrant": "A person who comes to live permanently in a foreign country.",
      "Emigrant": "A person who leaves their own country to settle permanently in another.",
      "Refugee": "A person forced to leave their country due to war, persecution, or disaster.",
      "Suburban": "Residential areas outside the main city center.",
      "Metropolitan": "Relating to a large city and its surrounding areas.",
      "Agricultural": "Relating to farming and the cultivation of crops.",
      "Remote": "Far away from populated areas or difficult to access.",
      "Progress": "Forward movement toward improvement or advancement.",
      "Growth": "An increase in size, amount, or degree.",
      "Advancement": "Development or improvement in a particular area."
    };

    return definitions[selectedText] || `Definition not available for "${selectedText}".`;
  };



  if (!gameStarted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-4xl shadow-xl border-0 bg-white">
          <CardHeader className="text-center pb-6">
            <div className="mx-auto mb-6 flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_earth-terms-game/artifacts/5igmym80_GeogOn%2BLogo%2B2.png" 
                alt="GeogOn Logo" 
                className="w-24 h-24 object-contain transition-transform duration-300 hover:scale-110"
              />
            </div>
            <CardTitle className="text-4xl font-bold text-slate-800 mb-3">
              The GeogOn Helper
            </CardTitle>
            <p className="text-slate-600 text-xl">
              Choose your challenge level and test your geography knowledge
            </p>
          </CardHeader>
          <CardContent className="pt-2">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <span className="text-red-700">{error}</span>
              </div>
            )}
            
            {showCategoryTypeSelection ? (
              igcseMenu ? (
                // IGCSE sub-menu: choose between Keyword Quiz and Question Helper
                <div className="space-y-6" data-testid="igcse-menu">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="text-2xl font-bold text-slate-800">IGCSE Geography</h3>
                      <p className="text-slate-600">Choose a learning activity</p>
                    </div>
                    <Button variant="outline" onClick={() => setIgcseMenu(false)} data-testid="igcse-menu-back-btn">
                      Back to Level Selection
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Button
                      onClick={() => { setIgcseMenu(false); selectCategoryType('IGCSE'); }}
                      className="p-6 h-auto text-left bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white border-0 shadow-xl transition-all duration-300 transform hover:scale-105 rounded-xl whitespace-normal"
                      data-testid="igcse-keyword-quiz-btn"
                    >
                      <div className="flex flex-col space-y-2 w-full">
                        <span className="text-xl md:text-2xl font-bold leading-tight">Keyword Quiz</span>
                        <p className="text-sm text-white/90 leading-relaxed">
                          Multiple-choice practice on IGCSE geography keywords and definitions.
                        </p>
                      </div>
                    </Button>
                    <Button
                      onClick={() => navigate('/igcse-geography/question-helper/setup')}
                      className="p-6 h-auto text-left bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 text-white border-0 shadow-xl transition-all duration-300 transform hover:scale-105 rounded-xl whitespace-normal"
                      data-testid="igcse-question-helper-btn"
                    >
                      <div className="flex flex-col space-y-2 w-full">
                        <span className="text-xl md:text-2xl font-bold leading-tight">CIE IGCSE Question Helper</span>
                        <p className="text-sm text-white/90 leading-relaxed">
                          Learn how Cambridge questions are marked by identifying points, development, explanations, place-specific detail and evaluation.
                        </p>
                      </div>
                    </Button>
                  </div>
                </div>
              ) : (
              // Step 1: Choose between IGCSE or KS3
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h3 className="text-3xl font-bold text-slate-800 mb-4">Choose Your Geography Level</h3>
                  <p className="text-lg text-slate-600">
                    Select the curriculum level you'd like to be tested on
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Button
                    onClick={() => setIgcseMenu(true)}
                    disabled={loading || getIGCSECategories().length === 0}
                    className="p-6 h-auto text-left bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white border-0 shadow-xl transition-all duration-300 transform hover:scale-105 disabled:transform-none disabled:opacity-50 rounded-xl overflow-hidden whitespace-normal"
                    data-testid="select-level-igcse-btn"
                  >
                    <div className="flex flex-col space-y-2 w-full">
                      <Badge variant="secondary" className="bg-white/20 text-white border-0 text-xs px-2 py-1 self-start">
                        {getIGCSECategories().length} topics
                      </Badge>
                      <span className="text-xl md:text-2xl font-bold leading-tight">IGCSE Geography</span>
                      <p className="text-sm text-white/90 leading-relaxed">
                        Advanced geography topics including changing environments, tectonic hazards, climate change, and development
                      </p>
                      <p className="text-xs text-white/75">
                        Perfect for IGCSE level students and above
                      </p>
                    </div>
                  </Button>

                  <Button
                    onClick={() => selectCategoryType('IBDP')}
                    disabled={loading || getIBDPCategories().length === 0}
                    className="p-6 h-auto text-left bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white border-0 shadow-xl transition-all duration-300 transform hover:scale-105 disabled:transform-none disabled:opacity-50 rounded-xl overflow-hidden whitespace-normal"
                    data-testid="select-level-ibdp-btn"
                  >
                    <div className="flex flex-col space-y-2 w-full">
                      <Badge variant="secondary" className="bg-white/20 text-white border-0 text-xs px-2 py-1 self-start">
                        {getIBDPCategories().length} topics
                      </Badge>
                      <span className="text-xl md:text-2xl font-bold leading-tight">IBDP Geography</span>
                      <p className="text-sm text-white/90 leading-relaxed">
                        IB Diploma core units, optional themes, and HL extensions covering climate, resources, hazards, and global interactions
                      </p>
                      <p className="text-xs text-white/75">
                        Perfect for IB Diploma SL and HL students
                      </p>
                    </div>
                  </Button>

                  <Button
                    onClick={() => selectCategoryType('KS3')}
                    disabled={loading || getKS3Categories().length === 0}
                    className="p-6 h-auto text-left bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 text-white border-0 shadow-xl transition-all duration-300 transform hover:scale-105 disabled:transform-none disabled:opacity-50 rounded-xl overflow-hidden whitespace-normal"
                    data-testid="select-level-ks3-btn"
                  >
                    <div className="flex flex-col space-y-2 w-full">
                      <Badge variant="secondary" className="bg-white/20 text-white border-0 text-xs px-2 py-1 self-start">
                        {getKS3Categories().length} topics
                      </Badge>
                      <span className="text-xl md:text-2xl font-bold leading-tight">KS3 Geography</span>
                      <p className="text-sm text-white/90 leading-relaxed">
                        Foundation geography covering rivers, coasts, plate tectonics, development, and regional studies
                      </p>
                      <p className="text-xs text-white/75">
                        Perfect for Key Stage 3 students (ages 11-14)
                      </p>
                    </div>
                  </Button>
                </div>
              </div>
              )
            ) : showCategorySelection ? (
              // Step 2: Select specific topics within chosen category type
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-semibold text-slate-800">
                      Select {categoryType} Topics
                    </h3>
                    <p className="text-sm text-slate-600 mt-1">
                      Choose the specific topics you want to be tested on
                    </p>
                  </div>
                  <Button
                    onClick={backToCategoryType}
                    variant="outline"
                  >
                    Back to Level Selection
                  </Button>
                </div>
                
                <div className="bg-slate-50 p-4 rounded-lg mb-6">
                  <p className="text-slate-600 text-sm">
                    Selected topics: <span className="font-semibold text-slate-800">{selectedCategories.length}</span> • 
                    Questions available: <span className="font-semibold text-slate-800">{getAvailableQuestionsCount()}</span>
                  </p>
                </div>
                
                {/* Grid of checkable topic cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(categoryType === 'IGCSE' ? getIGCSECategories() : categoryType === 'IBDP' ? getIBDPCategories() : getKS3Categories()).map((category) => {
                    const isSelected = selectedCategories.includes(category);
                    const topicName = category.replace('IGCSE ', '').replace('IBDP ', '').replace('KS3 ', '');
                    
                    return (
                      <div
                        key={category}
                        onClick={() => toggleCategory(category)}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-md ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50 shadow-md'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <Checkbox
                                id={category}
                                checked={isSelected}
                                onCheckedChange={() => toggleCategory(category)}
                                className="pointer-events-none" // Prevent double-clicking since the whole card is clickable
                              />
                              <Badge 
                                variant="secondary" 
                                className={`text-xs ${
                                  categoryType === 'IGCSE' 
                                    ? 'bg-blue-100 text-blue-700' 
                                    : categoryType === 'IBDP'
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-emerald-100 text-emerald-700'
                                }`}
                              >
                                {categoryType === 'IBDP'
                                  ? (IBDP_TOPIC_META[category]?.label || 'IBDP')
                                  : categoryType}
                              </Badge>
                              {categoryType === 'IBDP' && IBDP_TOPIC_META[category]?.level && (
                                <Badge
                                  variant="outline"
                                  className={`text-xs border ${
                                    IBDP_TOPIC_META[category].level === 'HL only'
                                      ? 'border-rose-300 text-rose-700 bg-rose-50'
                                      : 'border-slate-300 text-slate-600 bg-slate-50'
                                  }`}
                                >
                                  {IBDP_TOPIC_META[category].level}
                                </Badge>
                              )}
                            </div>
                            <h4 className="font-medium text-slate-800 text-sm leading-tight mb-1">
                              {topicName}
                            </h4>
                            <p className="text-xs text-slate-600">
                              {getTopicDescription(topicName)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {selectedCategories.length > 0 && (
                  <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <p className="text-emerald-800 text-sm font-medium mb-2">
                      ✓ {selectedCategories.length} topics selected
                    </p>
                    <p className="text-emerald-700 text-xs">
                      Selected: {selectedCategories.map(cat => cat.replace('IGCSE ', '').replace('IBDP ', '').replace('KS3 ', '')).join(', ')}
                    </p>
                  </div>
                )}

                <div className="flex justify-center mt-6">
                  <Button
                    onClick={proceedToDifficulty}
                    disabled={selectedCategories.length === 0}
                    className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold disabled:opacity-50 transition-all duration-300 transform hover:scale-105"
                  >
                    Continue to Difficulty Selection →
                  </Button>
                </div>
              </div>
            ) : showDifficultySelection ? (
              // Step 3: Select difficulty level and number of questions
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-semibold text-slate-800">Select Difficulty Level</h3>
                    <p className="text-sm text-slate-600 mt-1">
                      {categoryType} • {selectedCategories.length} topics selected
                    </p>
                  </div>
                  <Button
                    onClick={backToCategories}
                    variant="outline"
                  >
                    Back to Topics
                  </Button>
                </div>

                <div className="bg-slate-50 p-4 rounded-lg mb-6">
                  <p className="text-slate-600 text-sm">
                    Questions available: <span className="font-semibold text-slate-800">{getAvailableQuestionsCount()}</span> • 
                    Some difficulty levels may be disabled if not enough questions are available
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {difficultyOptions.map((difficulty, index) => {
                    const isDisabled = loading || difficulty.questions > getAvailableQuestionsCount();
                    return (
                      <Button
                        key={index}
                        onClick={() => startGame(difficulty)}
                        disabled={isDisabled}
                        className={`p-6 h-auto text-left border-0 shadow-lg transition-all duration-300 transform hover:scale-105 disabled:transform-none disabled:opacity-40 disabled:cursor-not-allowed ${
                          isDisabled 
                            ? 'bg-gradient-to-r from-slate-400 to-slate-500 text-white' 
                            : `bg-gradient-to-r ${difficulty.color} hover:${difficulty.hoverColor} text-white`
                        }`}
                      >
                        <div className="flex flex-col space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xl font-bold">{difficulty.name}</span>
                            <Badge variant="secondary" className="bg-white/20 text-white border-0">
                              {difficulty.questions} questions
                            </Badge>
                          </div>
                          <p className="text-sm text-white/90 leading-relaxed">
                            {isDisabled ? 'Not enough questions available for this level' : difficulty.description}
                          </p>
                        </div>
                      </Button>
                    );
                  })}
                </div>
                
                {loading && (
                  <div className="flex items-center justify-center mt-6">
                    <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
                    <span className="ml-3 text-lg text-slate-600">Starting your quiz...</span>
                  </div>
                )}
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (quizComplete) {
    const perfectScore = score === gameQuestions.length;
    const scorePercentage = Math.round((score / gameQuestions.length) * 100);
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className={`w-full max-w-lg shadow-xl border-0 bg-white ${perfectScore ? 'ring-4 ring-yellow-400 ring-opacity-50' : ''}`}>
          <CardHeader className="text-center">
            <div className="mx-auto mb-6 flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_earth-terms-game/artifacts/5igmym80_GeogOn%2BLogo%2B2.png" 
                alt="GeogOn Logo" 
                className={`w-20 h-20 object-contain transition-transform duration-300 hover:scale-110 ${perfectScore ? 'animate-bounce' : ''}`}
              />
            </div>
            <CardTitle className={`text-3xl font-bold mb-2 ${perfectScore ? 'text-yellow-600' : 'text-slate-800'}`}>
              {perfectScore ? '🎉 PERFECT SCORE! 🎉' : 'Quiz Complete!'}
            </CardTitle>
            <p className={`text-lg mb-2 ${perfectScore ? 'text-yellow-700' : 'text-slate-600'}`}>
              {perfectScore ? 'Incredible! You got every single question right! 🌟🏆' : getScoreMessage()}
            </p>
            {selectedDifficulty && (
              <Badge variant="secondary" className={`text-lg px-4 py-2 ${perfectScore ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white animate-pulse' : `bg-gradient-to-r ${selectedDifficulty.color} text-white`} border-0`}>
                {selectedDifficulty.name} Level {perfectScore ? 'MASTERED!' : 'Completed'}
              </Badge>
            )}
          </CardHeader>
          <CardContent>
            <div className="text-center mb-8">
              <div className={`text-6xl font-bold mb-2 ${perfectScore ? 'text-yellow-500 animate-pulse' : 'text-emerald-600'}`}>
                {score}/{gameQuestions.length}
              </div>
              <div className={`text-xl mb-2 ${perfectScore ? 'text-yellow-700' : 'text-slate-600'}`}>
                {scorePercentage}% Score {perfectScore ? '🎯' : ''}
              </div>
              {selectedDifficulty && (
                <p className={`text-sm ${perfectScore ? 'text-yellow-600' : 'text-slate-500'}`}>
                  {selectedDifficulty.questions} questions • {selectedDifficulty.name} difficulty
                </p>
              )}
            </div>
            <Button 
              onClick={restartGame} 
              className={`w-full py-6 text-lg font-semibold transition-all duration-300 transform hover:scale-105 ${
                perfectScore 
                  ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700' 
                  : 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
              }`}
            >
              <RotateCcw className="w-5 h-5 mr-2" />
              Play Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentQuestion = gameQuestions[currentQuestionIndex];
  if (!currentQuestion) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border-0 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_quizgeography/artifacts/mfs2df6h_GeogOn_G.png" 
                  alt="GeogOn G Logo" 
                  className="w-10 h-10 object-contain transition-transform duration-300 hover:scale-110"
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-800">The GeogOn Helper</h1>
                <div className="flex items-center gap-3">
                  <p className="text-slate-600">Question {currentQuestionIndex + 1} of {gameQuestions.length}</p>
                  {selectedDifficulty && (
                    <Badge variant="secondary" className={`bg-gradient-to-r ${selectedDifficulty.color} text-white border-0 text-xs`}>
                      {selectedDifficulty.name}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Badge variant="secondary" className="text-lg px-4 py-2 bg-emerald-100 text-emerald-700">
                Score: {score}/{gameQuestions.length}
              </Badge>
              <Button
                onClick={restartGame}
                variant="outline"
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition-all duration-200"
              >
                <RotateCcw className="w-4 h-4" />
                Restart
              </Button>
            </div>
          </div>
          <Progress value={getProgressPercentage()} className="h-2" />
        </div>

        {/* Question Card */}
        <Card className="shadow-xl border-0 bg-white mb-6">
          <CardHeader>
            <CardTitle className="text-xl text-slate-800 leading-relaxed">
              {currentQuestion.question}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {currentQuestion.options.map((option) => {
                let buttonClass = "w-full p-4 text-left text-lg border-2 transition-all duration-300 transform hover:scale-105 ";
                
                if (answerSubmitted) {
                  if (option.isCorrect) {
                    buttonClass += "bg-emerald-50 border-emerald-500 text-emerald-700";
                  } else if (selectedAnswer === option.id && !option.isCorrect) {
                    buttonClass += "bg-red-50 border-red-500 text-red-700";
                  } else {
                    buttonClass += "bg-slate-50 border-slate-200 text-slate-500";
                  }
                } else if (selectedAnswer === option.id) {
                  buttonClass += "bg-slate-100 border-slate-400 text-slate-800";
                } else {
                  buttonClass += "bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300";
                }

                return (
                  <Button
                    key={option.id}
                    variant="outline"
                    className={buttonClass}
                    onClick={() => handleAnswerSelect(option.id)}
                    disabled={answerSubmitted}
                    data-testid={`quiz-option-${option.id}`}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-slate-600">{option.id}.</span>
                        <span>{option.text}</span>
                      </div>
                      {answerSubmitted && option.isCorrect && (
                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                      )}
                      {answerSubmitted && selectedAnswer === option.id && !option.isCorrect && (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                    </div>
                  </Button>
                );
              })}
            </div>

            {showResult && (() => {
              const selectedOption = currentQuestion.options.find(opt => opt.id === selectedAnswer);
              const isCorrect = selectedOption?.isCorrect;
              const correctOption = currentQuestion.options.find(opt => opt.isCorrect);
              const selectedDefinition = selectedOption?.definition || getSelectedAnswerExplanation(selectedOption?.text);
              return (
              <div className="mt-6 space-y-4" data-testid="answer-feedback">
                {/* Result indicator */}
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-800">
                    {isCorrect ? "✅ Correct!" : "❌ Incorrect"}
                  </p>
                </div>

                {isCorrect ? (
                  /* Congratulations message for a correct answer */
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="correct-feedback">
                    <p className="text-emerald-800 font-semibold mb-1">
                      🎉 Well done! "{selectedOption?.text}" is the correct answer.
                    </p>
                    <p className="text-emerald-700 text-sm leading-relaxed">
                      {selectedDefinition}
                    </p>
                  </div>
                ) : (
                  /* Definition of the wrong answer the user selected */
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg" data-testid="incorrect-feedback">
                    <p className="text-slate-800 font-medium mb-2">
                      You selected: <span className="font-semibold">{selectedOption?.text}</span>
                    </p>
                    <p className="text-slate-700 text-sm leading-relaxed mb-3">
                      {selectedDefinition}
                    </p>
                    <p className="text-emerald-800 text-sm">
                      The correct answer was: <span className="font-semibold">{correctOption?.text}</span>
                    </p>
                  </div>
                )}

                {/* Next Question Button */}
                <div className="mt-6">
                  <Button 
                    onClick={nextQuestion}
                    data-testid="next-question-btn"
                    className="w-full py-3 text-lg font-semibold bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 transition-all duration-300"
                  >
                    {currentQuestionIndex < gameQuestions.length - 1 ? 'Next Question' : 'Complete Quiz'}
                  </Button>
                </div>
              </div>
              );
            })()}

            {!answerSubmitted && (
              <div className="mt-6">
                <Button 
                  onClick={submitAnswer}
                  disabled={!selectedAnswer || loading}
                  data-testid="submit-answer-btn"
                  className="w-full py-3 text-lg font-semibold bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 disabled:from-slate-300 disabled:to-slate-400 transition-all duration-300"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Answer'
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default QuizGame;